"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

/** How the middle column of a search session renders its profiles. */
export type SearchResultsView = "list" | "table";

interface TalentSearchContextValue {
  currentSearchId: string | null;
  currentProjectId: string | null;
  filtersOpen: boolean;
  agentOpen: boolean;
  /** Cards (`list`) or the criteria table (`table`). */
  resultsView: SearchResultsView;
  /**
   * Switch the results view. The first entry into the table collapses the
   * filter panel and the assistant so the table gets the width; after that,
   * switching never touches the panels — they keep whatever state the user
   * last left them in.
   */
  setResultsView: (view: SearchResultsView) => void;
  pendingSearchCreate: boolean;
  setCurrentSearchId: (searchId: string | null) => void;
  setCurrentProjectId: (projectId: string | null) => void;
  toggleFilters: () => void;
  toggleAgent: () => void;
  /** Open the filters panel (closes the assistant in the laptop range). */
  showFilters: () => void;
  /** Open the assistant panel (closes filters in the laptop range). */
  showAssistant: () => void;
  startPendingSearch: () => void;
  clearPendingSearch: () => void;
  resetSearch: () => void;
  enterRootMode: () => void;
  hydrateSearch: (searchId: string) => void;
}

/**
 * Laptop range (md … just under 2xl) where only one of the filters / assistant
 * side panels may be open at a time, so the in-flow panels don't squeeze the
 * results list from both sides. At ≥2xl both may stay open; below md the mobile
 * drawers handle layout. Checked at toggle time (a user gesture), so reading
 * window width directly is fine.
 */
const PANEL_EXCLUSIVE_MIN_WIDTH = 768;
const PANEL_EXCLUSIVE_MAX_WIDTH = 1536;

function isPanelExclusiveWidth() {
  if (typeof window === "undefined") return false;
  const w = window.innerWidth;
  return w >= PANEL_EXCLUSIVE_MIN_WIDTH && w < PANEL_EXCLUSIVE_MAX_WIDTH;
}

const TalentSearchContext = createContext<TalentSearchContextValue | null>(null);

export function TalentSearchProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [currentSearchId, setCurrentSearchId] = useState<string | null>(null);
  const [currentProjectId, setCurrentProjectId] = useState<string | null>(null);
  // Defaults are assistant-first: on a refresh / direct link the assistant is
  // the panel shown first. On laptops (md … <2xl) it's the single open panel
  // (filters start collapsed); at ≥2xl both open; below md the mobile drawers
  // stay closed. Hero entry points override this per intent via showFilters()
  // (started from filters) / showAssistant() (started from a prompt or JD).
  const [filtersOpen, setFiltersOpen] = useState(
    () =>
      typeof window === "undefined" ||
      window.innerWidth >= PANEL_EXCLUSIVE_MAX_WIDTH,
  );
  const [agentOpen, setAgentOpen] = useState(
    () =>
      typeof window === "undefined" ||
      window.innerWidth >= PANEL_EXCLUSIVE_MIN_WIDTH,
  );
  const [pendingSearchCreate, setPendingSearchCreate] = useState(false);
  const [resultsView, setResultsViewState] =
    useState<SearchResultsView>("list");
  // Panel state is shared across views and otherwise left alone by view
  // switches: only the *first* entry into the table collapses both side
  // panels (as a width-friendly default). Whatever the user opens afterwards
  // — in either view — survives every subsequent switch.
  const tableEnteredRef = useRef(false);
  const setResultsView = useCallback(
    (view: SearchResultsView) => {
      if (view === resultsView) return;
      if (view === "table" && !tableEnteredRef.current) {
        tableEnteredRef.current = true;
        setFiltersOpen(false);
        setAgentOpen(false);
      }
      setResultsViewState(view);
    },
    [resultsView],
  );

  const toggleFilters = useCallback(() => {
    const opening = !filtersOpen;
    setFiltersOpen(opening);
    // Laptop range: opening filters closes the assistant so the list keeps room.
    if (opening && isPanelExclusiveWidth()) setAgentOpen(false);
  }, [filtersOpen]);

  const toggleAgent = useCallback(() => {
    const opening = !agentOpen;
    setAgentOpen(opening);
    // Laptop range: opening the assistant closes filters so the list keeps room.
    if (opening && isPanelExclusiveWidth()) setFiltersOpen(false);
  }, [agentOpen]);

  // Intent-driven openers used by the hero when a search is started. They set an
  // absolute open state (not a toggle) and enforce laptop-range exclusivity.
  const showFilters = useCallback(() => {
    setFiltersOpen(true);
    if (isPanelExclusiveWidth()) setAgentOpen(false);
  }, []);

  const showAssistant = useCallback(() => {
    setAgentOpen(true);
    if (isPanelExclusiveWidth()) setFiltersOpen(false);
  }, []);

  // Enforce exclusivity on resize too, not only on toggle: if the window
  // shrinks into the laptop range (md … <2xl) while both panels are open,
  // close one so the in-flow panels don't squeeze the list from both sides.
  // Assistant-first — keep the assistant, collapse filters. The listener is
  // only attached while both are open, and detaches as soon as one closes.
  useEffect(() => {
    if (!(filtersOpen && agentOpen)) return;
    const onResize = () => {
      if (isPanelExclusiveWidth()) setFiltersOpen(false);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [filtersOpen, agentOpen]);

  const startPendingSearch = useCallback(() => {
    setPendingSearchCreate(true);
  }, []);

  const clearPendingSearch = useCallback(() => {
    setPendingSearchCreate(false);
  }, []);

  const enterRootMode = useCallback(() => {
    setCurrentSearchId(null);
    setPendingSearchCreate(false);
    // The hero has no table; start the next search on cards.
    setResultsViewState("list");
    tableEnteredRef.current = false;
  }, []);

  const resetSearch = useCallback(() => {
    setCurrentSearchId(null);
    setCurrentProjectId(null);
    setPendingSearchCreate(false);
  }, []);

  const hydrateSearch = useCallback((searchId: string) => {
    setCurrentSearchId(searchId);
    setPendingSearchCreate(false);
  }, []);

  const value = useMemo<TalentSearchContextValue>(
    () => ({
      currentSearchId,
      currentProjectId,
      filtersOpen,
      agentOpen,
      resultsView,
      setResultsView,
      pendingSearchCreate,
      setCurrentSearchId,
      setCurrentProjectId,
      toggleFilters,
      toggleAgent,
      showFilters,
      showAssistant,
      startPendingSearch,
      clearPendingSearch,
      resetSearch,
      enterRootMode,
      hydrateSearch,
    }),
    [
      agentOpen,
      clearPendingSearch,
      currentProjectId,
      currentSearchId,
      enterRootMode,
      filtersOpen,
      hydrateSearch,
      pendingSearchCreate,
      resetSearch,
      resultsView,
      setResultsView,
      showAssistant,
      showFilters,
      startPendingSearch,
      toggleAgent,
      toggleFilters,
    ],
  );

  return (
    <TalentSearchContext.Provider value={value}>
      {children}
    </TalentSearchContext.Provider>
  );
}

export function useTalentSearch() {
  const context = useContext(TalentSearchContext);
  if (!context) {
    throw new Error("useTalentSearch must be used within TalentSearchProvider");
  }
  return context;
}

export function useOptionalTalentSearch() {
  return useContext(TalentSearchContext);
}
